import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { parsePhoneNumber } from "awesome-phonenumber";

dayjs.extend(customParseFormat);

const validateOIB = (str: string): boolean => {
  if (str.length !== 11 || Number.isNaN(parseInt(str, 10))) {
    return false
  }
  const digits = str.split('').map(Number);
  const controlNumber = digits.pop();
  const a = digits.reduce((acc, digit) => (((acc + digit) % 10 || 10) * 2) % 11, 10);
  const calculatedControlNumber = 11 - a === 10 ? 0 : 11 - a;
  return controlNumber === calculatedControlNumber;
};

export const isNumberValid = (value: string, regionCode: string) => {
  try {
    return parsePhoneNumber(value, { regionCode }).valid;
  } catch (_) {
    return false;
  }
};

// Dio e-mail adrese prije @ (local part) ne smije sadržavati dijakritičke/ne-ASCII znakove (č, ć, ž, š, đ...).
// Domena smije sadržavati dijakritike (npr. ivan@čokolada.hr), pa provjeravamo samo dio prije posljednjeg @.
export const hasDiacriticsBeforeAt = (email: string): boolean => {
  const s = String(email ?? '');
  const at = s.lastIndexOf('@');
  const local = at === -1 ? s : s.slice(0, at);
  // eslint-disable-next-line no-control-regex -- namjerno: sve izvan ASCII raspona (uklj. kontrolne znakove) je nedozvoljeno u local dijelu
  return /[^\x00-\x7F]/.test(local);
};

// Konstanta koja mapira vrste validacija na funkcije za provjeru
export const validationRules: { [key: string]: (value: any, params?: { [key: string]: any }) => boolean } = {
  required: (value: any) => value ? !!value.trim() : false,
  requiredChoice: (value: any) => value !== undefined && value !== null && value !== '',
  requiredIf: (value: any, params?: { [key: string]: any }) =>
    params?.conditionValue ? (value !== undefined && value !== null && value !== '') : true,
  validMail: (value: any) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()),
  validMailNoDiacritics: (value: any) => !hasDiacriticsBeforeAt(String(value ?? '').trim()),
  validHouseNumber: (value: any) => /^.{0,5}$/.test(value.trim()),
  validPostalCode: (value: any) => value.trim().length === 5,
  validFullName: (value: any) => value.trim().split(' ').length >= 2,
  validDate: (value: any) => dayjs(value, 'YYYY-MM-DD', true).isValid(), 
  validOIB: (value: any) => validateOIB(value.trim()),
  validMBO: (value: any) => /^.{9}$/.test(value.trim()),
  validPhoneNumber: (value: any, params?: { [key: string]: any }) => isNumberValid(value, params?.regionCode),
  validStreets: (value: any, params?: { [key: string]: any }) => params?.streetsFound,
  validInsurancePrice: (value: any) => Number(String(value).trim().replace(',', '.')) > 0,
};

// Generiraj tip iz ključeva validationRules objekta
export type ValidationType = keyof typeof validationRules;

// Definiraj tip za validacijsko pravilo
export interface Validation {
  type: ValidationType;
  message: string;
  params?: { [key: string]: any };
}
